import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, inject, input, output, TemplateRef, viewChild, ViewContainerRef } from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';

/** Cascades the initial position of consecutively opened windows so they do not hide each other. */
let openedWindows = 0;

/**
 * Non-modal, draggable window: title bar (drag handle) + close button + projected content.
 * The page behind stays clickable. Dragging is done by @angular/cdk/drag-drop (cdkDrag + cdkDragHandle).
 *
 * The window is rendered into a CDK overlay (no backdrop) - the same layer nz-modal / nz-tooltip use - so
 * stacking is decided by opening order, not by magic z-index numbers: it is above a scan dialog that was open
 * before it, and a confirmation dialog opened from inside the window is above the window.
 *
 * The window is removed together with its host, so the host shows it with an @if and hides it on (closed). Focus moves
 * into the window when it opens and back to the previously focused element when it goes away.
 *
 * Usage:
 *   @if (open()) {
 *     <app-floating-window [title]="'Scan #5 - Tags'" (closed)="open.set(false)"> ...content... </app-floating-window>
 *   }
 */
@Component({
  selector: 'app-floating-window',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkDrag, CdkDragHandle, NzButtonModule, NzIconModule],
  template: `
    <ng-template #windowTpl>
      <div class="fw" cdkDrag role="dialog" tabindex="-1" [attr.aria-label]="title()" [style.width]="width()" (keydown.escape)="onEscape($event)">
        <div class="fw-title" cdkDragHandle>
          <span class="fw-title-text">{{ title() }}</span>
          <button nz-button nzType="text" nzSize="small" type="button" class="fw-close" i18n-aria-label aria-label="Close" (click)="closed.emit()">
            <span nz-icon nzType="close"></span>
          </button>
        </div>
        <div class="fw-body"><ng-content></ng-content></div>
      </div>
    </ng-template>
  `,
  styles: [
    `
      .fw {
        position: relative;
        max-width: 95vw;
        max-height: 90vh;
        display: flex;
        flex-direction: column;
        background: #fff;
        border: 1px solid #d9d9d9;
        border-radius: 6px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
        outline: none;
      }
      .fw-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 6px 10px;
        background: #2d2d2d;
        border-radius: 6px 6px 0 0;
        cursor: grab;
        user-select: none;
        flex-shrink: 0;
      }
      .fw-title:active {
        cursor: grabbing;
      }
      .fw-title-text {
        font-size: 12px;
        font-weight: 600;
        color: #ccc;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .fw-close {
        color: #aaa !important;
        flex-shrink: 0;
      }
      .fw-close:hover {
        color: #fff !important;
      }
      .fw-body {
        padding: 10px 12px;
        overflow: auto;
        min-height: 0;
      }
    `,
  ],
})
export class FloatingWindowComponent implements AfterViewInit {
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly windowTemplate = viewChild.required<TemplateRef<unknown>>('windowTpl');
  private overlayRef: OverlayRef | null = null;
  private previouslyFocused: HTMLElement | null = null;

  readonly title = input.required<string>();
  /** Any CSS width. */
  readonly width = input<string>('420px');

  /** Emitted by the close button and by Esc while focus is inside the window. */
  readonly closed = output<void>();

  private readonly slot = openedWindows++ % 6;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.overlayRef?.dispose();
      this.previouslyFocused?.focus?.(); // give the keyboard back to whoever opened the window
    });
  }

  ngAfterViewInit(): void {
    const left = Math.round(window.innerWidth * 0.25) + this.slot * 28;
    const top = Math.round(window.innerHeight * 0.12) + this.slot * 28;
    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay.position().global().left(`${left}px`).top(`${top}px`),
      hasBackdrop: false,
    });
    this.previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.overlayRef.attach(new TemplatePortal(this.windowTemplate(), this.viewContainerRef));
    // The overlay lives at the end of <body>: without this a keyboard user would have to tab through the whole page,
    // and Esc (handled while focus is inside) would not work right after opening.
    this.overlayRef.overlayElement.querySelector<HTMLElement>('.fw')?.focus();
  }

  protected onEscape(event: Event): void {
    event.stopPropagation();
    this.closed.emit();
  }
}
